import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { getPosts } from '../services/api';
import PostCard from './PostCard';
import CreatePost from './CreatePost';
import AdBanner from './AdBanner';

function PostSkeleton({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, idx) => (
        <article key={idx} className="post-card post-skeleton" aria-hidden="true">
          <div className="skeleton skeleton-meta" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line skeleton-line-short" />
          <div className="skeleton skeleton-media" />
        </article>
      ))}
    </>
  );
}

export default function PostList({ selectedCountry = '', selectedPlace = '' }) {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('latest');
  const [query, setQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [queuedNewPosts, setQueuedNewPosts] = useState([]);
  const [feedVideosMuted, setFeedVideosMuted] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const searchInputRef = useRef(null);
  const sortRef = useRef(null);
  const sentinelRef = useRef(null);
  const postsRef = useRef([]);

  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);

  useEffect(() => {
    if (!sortOpen) return;
    function handleOutside(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [sortOpen]);

  const loadPosts = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const data = await getPosts(p, 20, sortBy, selectedCountry, selectedPlace);
      setPosts((prev) => {
        if (p === 1) return data.posts;

        // Prevent accidental duplicates if the same page is requested twice
        const seen = new Set(prev.map((item) => item._id || item.id));
        const uniqueNew = data.posts.filter((item) => !seen.has(item._id || item.id));
        return [...prev, ...uniqueNew];
      });
      setHasMore(p < data.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, selectedCountry, selectedPlace]);

  useEffect(() => {
    setPosts([]);
    setQueuedNewPosts([]);
    setPage(1);
    setHasMore(true);
  }, [sortBy, selectedCountry, selectedPlace]);

  useEffect(() => {
    loadPosts(page);
  }, [page, loadPosts]);

  useEffect(() => {
    if (!sentinelRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first.isIntersecting) return;
        if (loading || !hasMore || error) return;
        setPage((prev) => prev + 1);
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore, error]);

  useEffect(() => {
    // Auto-refresh only when viewing latest feed so order expectations stay consistent.
    if (sortBy !== 'latest') return undefined;

    const timer = setInterval(async () => {
      try {
        const data = await getPosts(1, 10, 'latest', selectedCountry, selectedPlace);
        const incoming = Array.isArray(data.posts) ? data.posts : [];
        if (!incoming.length) return;

        const seen = new Set(postsRef.current.map((p) => p._id || p.id));
        const unseen = incoming.filter((p) => !seen.has(p._id || p.id));
        if (!unseen.length) return;

        setQueuedNewPosts((prev) => {
          const prevIds = new Set(prev.map((p) => p._id || p.id));
          const deduped = unseen.filter((p) => !prevIds.has(p._id || p.id));
          return deduped.length ? [...deduped, ...prev] : prev;
        });
      } catch (_) {
        // Silent fail: periodic refresh should not disrupt current feed state.
      }
    }, 12000);

    return () => clearInterval(timer);
  }, [sortBy, selectedCountry, selectedPlace]);

  function handlePostCreated(newPost) {
    // Optimistically prepend the new post without a full reload
    if (sortBy === 'latest') {
      setPosts((prev) => [newPost, ...prev]);
    }
  }

  function showQueuedPosts() {
    if (!queuedNewPosts.length) return;
    setPosts((prev) => [...queuedNewPosts, ...prev]);
    setQueuedNewPosts([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const normalizedQuery = query.trim().toLowerCase();
  const visiblePosts = posts.filter((post) => {
    if (!normalizedQuery) return true;
    const haystack = `${post.username || ''} ${post.content || ''}`.toLowerCase();
    return haystack.includes(normalizedQuery);
  });

  return (
    <div className="post-list-container">
      <CreatePost onPostCreated={handlePostCreated} selectedCountry={selectedCountry} />

      {error && (
        <div className="error-banner">
          {error}{' '}
          <button className="btn-ghost" onClick={() => loadPosts(page)}>
            Retry
          </button>
        </div>
      )}

      <section className="feed-controls" aria-label="Feed controls">
        <div className={`feed-search-wrap${searchOpen ? ' feed-search-open' : ''}`}>
          <label className="feed-search-icon-label" htmlFor="searchFeed" aria-label="Search">
            🔍
          </label>
          <input
            ref={searchInputRef}
            id="searchFeed"
            className="feed-search-input"
            type="text"
             placeholder="Find topics, words, usernames..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onBlur={() => { if (!query) setSearchOpen(false); }}
          />
        </div>
        <button
          className="btn-control feed-search-mobile-btn"
          aria-label="Search"
          onClick={() => {
            setSearchOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 50);
          }}
        >
          🔍
        </button>
        <div className={`feed-control-sort${sortOpen ? ' feed-sort-open' : ''}`} ref={sortRef}>
          <button
            type="button"
            className="btn-control feed-sort-btn"
            aria-label="Sort posts"
            aria-expanded={sortOpen}
            onClick={() => setSortOpen((prev) => !prev)}
          >
            <span className="feed-sort-icon">⇅</span>
            <span className="feed-sort-current">{{latest:'Latest',liked:'Most Liked',discussed:'Most Discussed'}[sortBy]}</span>
          </button>
          {sortOpen && (
            <ul className="feed-sort-dropdown" role="listbox" aria-label="Sort options">
              {[['latest','Latest'],['liked','Most Liked'],['discussed','Most Discussed']].map(([val, label]) => (
                <li key={val}>
                  <button
                    type="button"
                    className={`feed-sort-option${sortBy === val ? ' feed-sort-option-active' : ''}`}
                    role="option"
                    aria-selected={sortBy === val}
                    onClick={() => { setSortBy(val); setSortOpen(false); }}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {selectedCountry && (
        <p className="loading-text">Filtering by country: {selectedCountry}</p>
      )}
      {selectedPlace && (
        <p className="loading-text">Filtering by place: {selectedPlace}</p>
      )}

      {queuedNewPosts.length > 0 && (
        <div className="new-posts-banner" role="status" aria-live="polite">
          <button className="btn-primary" onClick={showQueuedPosts}>
            {queuedNewPosts.length} new post{queuedNewPosts.length > 1 ? 's' : ''} - tap to view
          </button>
        </div>
      )}

      {!loading && posts.length === 0 && !error && (
        <div className="empty-state">
          <p>No posts yet. Be the first to post anonymously.</p>
        </div>
      )}

      {!loading && posts.length > 0 && visiblePosts.length === 0 && (
        <div className="empty-state">
          <p>No posts match your current filters.</p>
        </div>
      )}

      <div className="post-feed">
        <section className="post-card welcome-panel" aria-label="About TheScandal">
          <p className="welcome-kicker">See What's Happening Around You.</p>
          <h1>Welcome to TheScandal</h1>
          <p>
            TheScandal is a community-driven platform that helps people stay informed about what is happening in
            their neighborhoods and communities around the world.
          </p>
          <p>
            On this platform, users can share posts, images, and videos about events, incidents, and situations
            happening around them. Other users can comment, discuss, and share information to help spread awareness
            and keep communities informed.
          </p>
          <p>
            TheScandal is designed with privacy in mind. The platform does not require registration, and users can
            post anonymously. Usernames are automatically generated to allow open participation while protecting privacy.
          </p>
          <p>
            Our goal is to create a space where people can share real-world updates, raise awareness about important
            issues, and help communities stay connected and informed.
          </p>
          <p>
            To maintain a respectful and safe environment, content that includes harassment, pornography, hate speech,
            or unrelated material may be removed by administrators.
          </p>
          <p>
            TheScandal exists to give communities a voice and to help people understand what is happening around them.
          </p>
          <div className="welcome-taglines" aria-label="Platform taglines">
            <span>Community Voices. Real Events.</span>
            <span>Stay Informed. Stay Aware.</span>
            <span>What's Happening, As It Happens.</span>
          </div>
          <p className="welcome-close">Stay informed. Share responsibly.</p>
        </section>

        {loading && posts.length === 0 && <PostSkeleton count={4} />}

        {visiblePosts.map((post, idx) => {
          const id = post._id || post.id;
          return (
          <Fragment key={id}>
            <PostCard
              post={post}
              selectedCountry={selectedCountry}
              feedVideosMuted={feedVideosMuted}
              onFeedVideosMutedChange={setFeedVideosMuted}
            />
            {(idx + 1) % 5 === 0 && <AdBanner />}
          </Fragment>
          );
        })}

        {loading && posts.length > 0 && <PostSkeleton count={2} />}
      </div>

      {posts.length > 0 && hasMore && (
        <div ref={sentinelRef} className="spinner" aria-live="polite">
          {loading ? 'Loading more posts…' : 'Scroll for more posts…'}
        </div>
      )}

      {posts.length > 0 && !hasMore && !loading && (
        <div className="empty-state">
          <p>🎉 You've reached the end.</p>
        </div>
      )}

    </div>
  );
}
